import type { Lift, Loadout, UpgradeSystem } from '@full-elevator/sim';
import { tierOf, UPGRADE_SYSTEMS } from '@full-elevator/sim';
import { liftLookOf } from '../view/liftParts.js';
import { clear, el, icon } from './dom.js';
import { infoButton } from './info.js';
import { partSvg } from './parts.js';

/**
 * The garage: one card per system, each showing the part you have fitted.
 *
 * Every entry is still a named part with a real number and a sentence about
 * what it lets you do — never a percentage. What is new is that the part has a
 * face: the fitted one large, the road to the best one along the bottom, the
 * next step in full and the one after as a silhouette, so the better part is
 * something to want before it is something to read about. Fitting one plays a
 * short install: the new part lands, sparks, and stamps "Installed".
 *
 * Since ADR-0027 it is one building's lift at a time: every building has its
 * own, bought separately, with its own parts, its own ceiling and its own
 * doors. The buildings reached so far sit along the top to switch between.
 */
const SYSTEM_LABEL: Record<UpgradeSystem, string> = {
  motor: 'Motor',
  cabin: 'Cabin',
  doors: 'Doors',
  cable: 'Cable',
};

export interface GarageState {
  readonly coins: number;
  /** The lift on the ramp: the building's own. */
  readonly lift: Lift;
  readonly loadout: Loadout;
  /** The system just fitted, so its card plays the install. */
  readonly fitted: UpgradeSystem | null;
  /** Whose lift this is. */
  readonly building: string;
  /** The buildings reached so far, each with a lift of its own to switch to. */
  readonly reached: readonly { readonly id: string; readonly name: string }[];
  /** The first part, fitted together (ADR-0041): the part the lesson is about. */
  readonly lesson?: { readonly system: UpgradeSystem } | null;
}

/** Each system's fact as an icon: the speed per floor, the doors, the seats, the ropes. */
const SYSTEM_ICON: Record<UpgradeSystem, string> = {
  motor: 'clock',
  cabin: 'person',
  doors: 'doors',
  cable: 'rope',
};

/**
 * A tier's number, short enough for a chip (PLAYTEST_LOG 2026-10-29): the first
 * figure of its headline, and for a rope the speed after the first floor as a
 * multiple ("6 floors in the time of 5" is ×1.2). The headline and its
 * sentence stay whole behind the card's "i".
 */
export function figureOf(system: UpgradeSystem, headline: string): string {
  if (system === 'cable') {
    const ratio = /(\d+) floors in the time of (\d+)/.exec(headline);
    if (!ratio) return '×1';
    const times = Number(ratio[1]) / Number(ratio[2]);
    return `×${Number(times.toFixed(2))}`;
  }
  return /[\d.]+s?/.exec(headline)?.[0] ?? headline;
}

export function renderGarage(
  body: HTMLElement,
  state: GarageState,
  onFit: (system: UpgradeSystem) => void,
  onPick: (buildingId: string) => void,
): void {
  clear(body);
  if (state.reached.length > 1) body.append(tabsFor(state, onPick));
  const parts = state.lift.parts;
  const name = (state.reached.find((b) => b.id === state.building)?.name ?? '').replace(
    /^The /,
    '',
  );
  body.append(
    el('div', { class: 'garage-lift', 'data-building': state.building }, [
      el('div', { class: 'garage-lift-head' }, [
        el('span', { class: 'garage-lift-name', text: `${name}’s lift` }),
        infoButton(`${name}’s lift`, [
          "You can't buy it all: choose what to fit first.",
          `Every building has its own lift. This one: ${parts.cabin[0]?.headline ?? ''}, up to ${parts.cabin[parts.cabin.length - 1]?.headline ?? ''}.`,
        ]),
      ]),
      // The lift as it is, in the indicator window: one icon and one number a part.
      el(
        'div',
        { class: 'garage-window' },
        UPGRADE_SYSTEMS.map((system) =>
          el('div', {}, [
            icon(SYSTEM_ICON[system]),
            el('span', { text: figureOf(system, tierOf(system, state.loadout, parts).headline) }),
          ]),
        ),
      ),
    ]),
  );
  const grid = el('div', { class: 'garage-grid' });
  for (const system of UPGRADE_SYSTEMS) {
    const part = card(system, state, onFit);
    // The lesson's part (ADR-0041): lit, and the garage opens on it.
    grid.append(part);
    if (state.lesson?.system === system) {
      part.dataset.lesson = 'target';
      requestAnimationFrame(() => part.scrollIntoView({ block: 'center' }));
    }
  }
  body.append(grid);
}

function tabsFor(state: GarageState, onPick: (buildingId: string) => void): HTMLElement {
  const tabs = el('div', { class: 'garage-tabs', role: 'tablist' });
  for (const building of state.reached) {
    const tab = el(
      'button',
      {
        class: 'garage-tab',
        type: 'button',
        role: 'tab',
        'data-building': building.id,
        'aria-selected': String(building.id === state.building),
      },
      // Short enough that four fit across a phone: Office, Grand Hotel, Transit, Skyline.
      [building.name.replace(/^The /, '').replace(/ (Tower|Centre)$/, '')],
    );
    tab.addEventListener('click', () => onPick(building.id));
    tabs.append(tab);
  }
  return tabs;
}

/** How a part at a tier is pictured in this lift: its building's finish, its tier's seats. */
function look(
  state: GarageState,
  index: number,
): { look: ReturnType<typeof liftLookOf>; seats: number } {
  const tier = state.lift.parts.cabin[Math.min(index, state.lift.parts.cabin.length - 1)];
  return { look: liftLookOf(state.building), seats: tier?.spec.capacity ?? 4 };
}

/**
 * One part: its picture, its name, how far up it is, its number, and one
 * button that says what the next part makes of that number and what it costs.
 * The next part's name and sentence are behind the "i".
 */
function card(
  system: UpgradeSystem,
  state: GarageState,
  onFit: (system: UpgradeSystem) => void,
): HTMLElement {
  const tiers = state.lift.parts[system];
  const installed = state.loadout[system] ?? 0;
  const fitted = tierOf(system, state.loadout, state.lift.parts);
  const next = tiers[installed + 1];
  const justFitted = state.fitted === system;

  const stage = el('div', { class: 'part-stage' }, [
    el('span', { class: 'part-spot', 'aria-hidden': 'true' }),
    partSvg(system, installed, 'part-art', look(state, installed)),
  ]);
  if (justFitted) {
    stage.append(el('span', { class: 'part-ring', 'aria-hidden': 'true' }), sparks());
    stage.append(el('span', { class: 'part-stamp', text: 'Installed!' }));
  }

  const pips = el('div', {
    class: 'tier-pips',
    'aria-label': `tier ${installed + 1} of ${tiers.length}`,
  });
  tiers.forEach((_, index) => {
    pips.append(el('span', { class: 'pip', 'data-on': String(index <= installed) }));
  });

  const root = el('div', {
    class: 'part-card',
    'data-system': system,
    'data-max': String(!next),
    'data-fitted': String(justFitted),
  });
  root.append(
    infoButton(
      next ? `Next: ${next.name}` : fitted.name,
      next
        ? [`${SYSTEM_LABEL[system]}: ${next.headline}.`, next.effect]
        : [`${fitted.headline}.`, 'The best this building’s lift can take.'],
    ),
    stage,
    el('div', { class: 'part-name', text: fitted.name }),
    el('div', { class: 'part-meta' }, [
      pips,
      el('span', { class: 'chip' }, [icon(SYSTEM_ICON[system]), figureOf(system, fitted.headline)]),
    ]),
  );

  if (next) {
    const affordable = state.coins >= next.cost;
    const buy = el(
      'button',
      {
        class: affordable ? 'primary part-buy' : 'part-buy',
        type: 'button',
        disabled: !affordable,
        'aria-label': `Fit ${next.name} for ${next.cost} coins`,
      },
      [
        el('span', { class: 'part-buy-up' }, [icon('up'), figureOf(system, next.headline)]),
        coinTag(next.cost),
      ],
    );
    buy.addEventListener('click', () => onFit(system));
    root.append(buy);
  } else {
    root.append(el('div', { class: 'part-max', text: 'Max' }));
  }
  return root;
}

function sparks(): HTMLElement {
  const burst = el('span', { class: 'part-burst', 'aria-hidden': 'true' });
  for (let k = 0; k < 12; k += 1) {
    burst.append(
      el('span', {
        class: k % 3 === 0 ? 'spark spark-dot' : 'spark',
        style: `--a:${k * 30 + ((k * 17) % 14)}deg; --d:${52 + ((k * 13) % 24)}px; --s:${7 + (k % 5)}px; --k:${(k % 4) * 0.03}s`,
      }),
    );
  }
  return burst;
}

function coinTag(cost: number): HTMLElement {
  const coin = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  coin.setAttribute('viewBox', '0 0 20 20');
  coin.setAttribute('aria-hidden', 'true');
  coin.innerHTML =
    '<circle cx="10" cy="10" r="8" fill="#e6c98a" stroke="#875a06" stroke-width="2"/>' +
    '<path d="M10 6v8" stroke="#875a06" stroke-width="2" stroke-linecap="round"/>';
  return el('span', { class: 'coin-tag' }, [coin, String(cost)]);
}
